import { SelectFieldBinder } from '../../binders/daq';
import { Gizmo } from '../../gizmos';
import {
  IGizmo,
  IGizmoCallbacks,
  IGizmoCatalogOptionV2,
  IGizmoDataV2,
  TGizmoData,
} from '../../types';

interface IParams {
  variable: string;
  data: TGizmoData;
  context: {
    answerGizmo: Gizmo;
  };
}

/**
 * Get datafrom a select_field gizmo
 *
 * @param {string} variable influx variable
 * @param {TGizmoData} data gizmo's data
 *
 * @returns
 */
async function selectFieldValues({ variable, data, context }: IParams) {
  const { answerGizmo } = context;
  const [uid, ...accesor] = variable.split('.');

  // console.log('sssssselectFieldValues->variable: ', variable, ', data: ', data);
  if (
    uid &&
    accesor[0] &&
    accesor[0] === 'metadata' &&
    answerGizmo &&
    data &&
    !Array.isArray(data)
  ) {
    // TODO: implement metadata functionality
    const selectFieldOptions = answerGizmo.getConfig().ops?.selectField;
    const catalogFid = selectFieldOptions?.catalogFid;

    if (catalogFid) {
      const catalog = answerGizmo.getCallbacks().getInfoCB({
        type: 'catalog',
        catalogFid,
      });

      if (Array.isArray(catalog.options)) {
        const selectedOption = catalog.options.find(
          (o: IGizmoCatalogOptionV2) => o.f_id === data.sod?.f_id
        );

        if (
          selectedOption?.answers &&
          accesor[1] &&
          selectFieldOptions?.metadata &&
          Array.isArray(selectFieldOptions.metadata)
        ) {
          let metadataGizmo: IGizmo | undefined;

          if (accesor[1].match(/^\s*q_?\d*\s*/g)) {
            metadataGizmo = selectFieldOptions.metadata.find(
              g => g.fid === accesor[1]
            );
          } else {
            metadataGizmo = selectFieldOptions.metadata.find(
              g => g.uid === accesor[1]
            );
          }

          if (metadataGizmo) {
            const mG: any = metadataGizmo;
            const fid = mG.fid || mG.f_id;
            const metadataValue: IGizmoDataV2 | undefined =
              selectedOption.answers[fid];
            if (metadataValue && metadataValue[0] && metadataValue[0].value) {
              return metadataValue[0].value;
            }
          }
        }
      } else if (!Array.isArray(catalog.options)) {
        // Metadata support for 'dependant_options'
        if (
          data.sod?.answers &&
          selectFieldOptions?.metadata &&
          Array.isArray(selectFieldOptions.metadata)
        ) {
          // We are going for any because we could have f_id or fid
          let metadataGizmo: any | undefined;

          if (accesor[1].match(/^\s*q_?\d*\s*/g)) {
            metadataGizmo = selectFieldOptions.metadata.find(
              g => g.fid === accesor[1]
            );
          } else {
            metadataGizmo = selectFieldOptions.metadata.find(
              g => g.uid === accesor[1]
            );
          }

          if (metadataGizmo) {
            const fid = metadataGizmo.fid || metadataGizmo.f_id;
            const metadataValue: IGizmoDataV2 | undefined =
              data.sod.answers[fid];

            if (metadataValue && metadataValue[0] && metadataValue[0].value) {
              return metadataValue[0].value;
            }
          }
        }
      }
    }
  } else if (uid && accesor[0] && accesor[0] === 'options' && answerGizmo) {
    const binder: SelectFieldBinder = answerGizmo.getBinder();

    if (binder) {
      return binder.getOptions().map(o => o.value);
    }
  } else if (
    uid &&
    accesor[0] &&
    accesor[0] === 'not_na' &&
    data &&
    !Array.isArray(data) &&
    data.v === 'NA' &&
    answerGizmo
  ) {
    // In here we're handling when a select_field has 'NA' as answer, we're going to "climb"
    // on the parent select_fields until we found a value that's not NA
    const config = answerGizmo.getConfig();
    if (config.ops?.selectField?.parentFieldFid) {
      const parentValue = getParentDataNotNA({
        parentGizmoFid: config.ops.selectField.parentFieldFid,
        gizmoCallbacks: answerGizmo.getCallbacks(),
      });

      if (parentValue !== undefined) {
        return parentValue;
      }
    }
  } else if (uid && data && !Array.isArray(data) && data.v) {
    return data.v;
  }

  return undefined;
}

interface IGetParentDataNotNAParams {
  parentGizmoFid: string;
  gizmoCallbacks: IGizmoCallbacks;
}

/**
 * Recursive function used to get data from the parent until it has a value different than NA
 *
 * @param {string} parentGizmoFid fid of the parent gizmo
 * @param {IGizmoCallbacks} gizmoCallbacks callbacks used to access parent data
 * @returns
 */
function getParentDataNotNA({
  parentGizmoFid,
  gizmoCallbacks,
}: IGetParentDataNotNAParams): any {
  const parentGizmo: Gizmo = gizmoCallbacks.getInfoCB({
    type: 'gizmo',
    fid: parentGizmoFid,
  });

  if (parentGizmo) {
    const data = parentGizmo.getData();
    if (data && !Array.isArray(data) && data.v) {
      const value = data.v;

      if (value == 'NA') {
        const config = parentGizmo.getConfig();

        if (config.ops?.selectField?.parentFieldFid) {
          return getParentDataNotNA({
            parentGizmoFid: config.ops.selectField.parentFieldFid,
            gizmoCallbacks,
          });
        } else {
          return value;
        }
      } else {
        return value;
      }
    }
  }
}

export default selectFieldValues;
